import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, IconButton, Box,
    TextField, Button, Slide, Typography, InputAdornment, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { changeEmployeePasswordReq } from '../../../../axios/request/SocietyController/CtrlEmployeeReqs/CtrlEmployeeReqs';
import { notify, NOTIFICATION_TYPE_SUCCESS } from '../../../../utils/notification';
import { employeeFullName } from './employeeConstants';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' },
    '& .MuiInputBase-input': { fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiFormHelperText-root': { fontSize: 11, marginLeft: 0 },
};

const MIN_LEN = 8;
const MAX_LEN = 100;
const EMPTY = { newPassword: '', confirmPassword: '' };

// The row to send: any of the person's society records works (one login covers them
// all), but X-Society-Id must be that record's own society.
const targetRecord = (person) => {
    const s = (person?.societies || []).find((r) => r?.employee_id != null);
    return s
        ? { employeeId: s.employee_id, societyId: s.society_id ?? s.society?.society_id }
        : { employeeId: person?.employee_id, societyId: person?.society_id ?? person?.society?.society_id };
};

/**
 * Admin sets a new login password for an employee — PUT /api/employees/:id/password.
 * Only offered when the person has a login (user_id) and isn't the signed-in admin.
 */
const ChangePasswordModal = ({ open, person, onClose }) => {
    const [form, setForm] = useState(EMPTY);
    const [show, setShow] = useState(false);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) { setForm(EMPTY); setErrors({}); setApiError(''); setShow(false); }
    }, [open]);

    const communities = (person?.societies || []).map((s) => s.society?.name).filter(Boolean);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
        setApiError('');
    };

    const validate = () => {
        const e = {};
        if (!form.newPassword) e.newPassword = 'New password is required';
        else if (form.newPassword.length < MIN_LEN) e.newPassword = `Must be at least ${MIN_LEN} characters`;
        else if (form.newPassword.length > MAX_LEN) e.newPassword = `Must be at most ${MAX_LEN} characters`;
        if (!form.confirmPassword) e.confirmPassword = 'Confirm password is required';
        else if (form.confirmPassword !== form.newPassword) e.confirmPassword = 'Passwords do not match';
        return e;
    };

    const handleSubmit = async (ev) => {
        ev?.preventDefault?.();
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }

        setSubmitting(true);
        setApiError('');
        try {
            const res = await changeEmployeePasswordReq({ ...targetRecord(person), ...form });
            if (res?.success === false) throw new Error(res?.message || 'Failed to change password');
            const revoked = res?.data?.revoked_sessions || 0;
            notify(NOTIFICATION_TYPE_SUCCESS, revoked
                ? `Password changed. Employee logged out of ${revoked} device${revoked === 1 ? '' : 's'}.`
                : 'Password changed successfully.');
            setForm(EMPTY); // don't keep the password around
            onClose?.();
        } catch (err) {
            // Stay open so the admin can correct and retry.
            setApiError(err?.message || 'Failed to change password');
        } finally {
            setSubmitting(false);
        }
    };

    const eye = (
        <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow((v) => !v)} edge="end" tabIndex={-1}>
                <Icon icon={show ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} style={{ fontSize: 17 }} />
            </IconButton>
        </InputAdornment>
    );

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} TransitionComponent={Transition}
            maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            <form onSubmit={handleSubmit} noValidate>
                {/* Header */}
                <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eef1f6' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, bgcolor: '#eef4ff', color: '#487fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon icon="solar:key-minimalistic-square-bold-duotone" style={{ fontSize: 21 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>Change Password</Typography>
                            <Typography noWrap sx={{ fontSize: 12, color: '#64748b' }}>
                                {employeeFullName(person)}{person?.email ? ` · ${person.email}` : ''}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} disabled={submitting} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <DialogContent sx={{ px: 2.5, py: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, p: 1.25, mb: 2, borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                        <Icon icon="mdi:alert-outline" style={{ fontSize: 17, color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                        <Typography sx={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                            This logs the employee out of all devices; they'll need the new password to sign in.
                            {' '}It is one login, so the change also applies
                            {communities.length > 1 ? ` in all ${communities.length} of their communities` : ' in every community they work in'}.
                        </Typography>
                    </Box>

                    <TextField label="New Password" type={show ? 'text' : 'password'} size="small" fullWidth autoFocus
                        autoComplete="new-password" sx={{ ...fieldSx, mb: 1.75 }}
                        value={form.newPassword} onChange={set('newPassword')}
                        error={!!errors.newPassword} helperText={errors.newPassword || `${MIN_LEN}–${MAX_LEN} characters`}
                        inputProps={{ maxLength: MAX_LEN }} InputProps={{ endAdornment: eye }} />
                    <TextField label="Confirm Password" type={show ? 'text' : 'password'} size="small" fullWidth
                        autoComplete="new-password" sx={fieldSx}
                        value={form.confirmPassword} onChange={set('confirmPassword')}
                        error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                        inputProps={{ maxLength: MAX_LEN }} />

                    {apiError && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.75, px: 1.25, py: 1, borderRadius: 2, bgcolor: '#fef2f2', color: '#b91c1c' }}>
                            <Icon icon="mdi:alert-circle-outline" style={{ fontSize: 16, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 12 }}>{apiError}</Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eef1f6', gap: 1 }}>
                    <Button onClick={onClose} disabled={submitting} variant="text"
                        sx={{ textTransform: 'none', fontSize: 13, color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={submitting} disableElevation
                        startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={{ textTransform: 'none', fontSize: 13, borderRadius: 2, px: 2 }}>
                        {submitting ? 'Changing…' : 'Change Password'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ChangePasswordModal;
